import React from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { Close } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { animationTransition } from '@shared/utils/animationTransition';
import type { TranslateFunction } from '@locales/generated';
import { mapToLabel, useFacetOptions } from '../facetOptions';
import type { FilterFacet } from '../Filter.types';
import { useToggleFilter } from '../useToggleFilter';

type ActiveFiltersRowProps = Readonly<{
  /** The applied filters no presented facet offers, from `unpresentedActiveFilters`. */
  compositeCodes: ReadonlyArray<string>;
}>;

type ActiveChip = Readonly<{ key: string; label: string; onRemove: () => void }>;

/**
 * The composite codes that are applied but that no presented facet offers as an option. The
 * nav's "Printed" link sets `format:paper`, and `format` is not a presented group at all — so
 * without a chip the grid is filtered by something with no control anywhere on the page. The
 * container computes it once, to decide whether the row is worth any space and to feed it.
 */
export const unpresentedActiveFilters = (facets: ReadonlyArray<FilterFacet>, activeFilters: ReadonlySet<string>): string[] => {
  const presented = new Set(facets.flatMap(facet => facet.options.map(option => option.compositeCode)));
  return Array.from(activeFilters).filter(compositeCode => !presented.has(compositeCode));
};

/**
 * One removable chip per applied filter that no presented facet offers. Everything else is
 * named where it is set — by its pill on desktop, under its section heading in the mobile
 * drawer — so a chip for it would say it twice. Mobile used to chip every filter; that wall of chips
 * went once the drawer named its selection (Arun, 2026-10-30, after Jeff flagged it).
 */
export const ActiveFiltersRow = ({ compositeCodes }: ActiveFiltersRowProps) => {
  const { t, tq } = useTranslation('stationery');
  const { labelByOption } = useFacetOptions();
  const toggleFilter = useToggleFilter();
  const tFilterOptions = t('dashboard', 'designsGallery', 'filterOptions') as Record<string, TranslateFunction>;

  const chips: ActiveChip[] = [];
  const filterChip = (compositeCode: string, label: string, facetGroupCode: string): ActiveChip => ({
    key: compositeCode,
    label,
    onRemove: () => toggleFilter(compositeCode, false, { source: 'activeFilterChip', facet: facetGroupCode }),
  });
  // An unpresented filter has no option to borrow a label from, so fall back through the
  // locale map and the index-derived display names before title-casing the bare value code.
  for (const compositeCode of compositeCodes) {
    const [groupCode, valueCode] = compositeCode.split(':');
    const label = tFilterOptions[`${groupCode} ${valueCode}`]?.() ?? mapToLabel(compositeCode, labelByOption) ?? `${valueCode.charAt(0).toUpperCase()}${valueCode.slice(1)}`;
    chips.push(filterChip(compositeCode, label, groupCode));
  }

  if (chips.length === 0) return null;

  return (
    <Flex
      as="ul"
      role="list"
      aria-label={tq`Active filters`}
      flexWrap="wrap"
      alignItems="center"
      gap={2}
      margin={0}
      padding={0}
      style={{ listStyle: 'none' }}
      data-testid="active-filters-row"
    >
      {chips.map(chip => (
        <Box
          as="li"
          key={chip.key}
        >
          <Box
            as="button"
            type="button"
            onClick={chip.onRemove}
            aria-label={tq`Remove filter: ${chip.label}`}
            display="inline-flex"
            alignItems="center"
            columnGap={2}
            height={32}
            paddingLeft={4}
            paddingRight={3}
            borderRadius={24}
            border="1px solid"
            borderColor="mono14"
            backgroundColor="mono14"
            color="white"
            typographyVariant="label2"
            transition={animationTransition('background-color')}
            _hover={{ backgroundColor: 'mono13' }}
          >
            {chip.label}
            <Close
              size={16}
              aria-hidden="true"
            />
          </Box>
        </Box>
      ))}
    </Flex>
  );
};
